import type { UserConfidence } from "./types";

/** Persona tag on a recorded answer — mirrors evaluation corpus organisation. */
export type AgentDemoPersona =
  | "generic"
  | "security"
  | "architect"
  | "operations"
  | "finops";

/** Metadata about the deterministic evaluation run that produced the fixture. */
export interface AgentDemoRecording {
  readonly evaluatedAt: string;
  readonly evaluationSchemaVersion: number;
  readonly turnsRecorded: number;
  readonly modelCalls: number;
  readonly gatesPassed: number;
  readonly gatesEvaluated: number;
}

/** Normalised evidence gap — source JSON may be a string or an object. */
export interface AgentDemoGap {
  readonly reason: string;
  readonly detail: string;
}

/** One pre-recorded Agent answer in the public demo bundle. */
export interface AgentDemoAnswer {
  readonly key: string;
  readonly persona: AgentDemoPersona;
  readonly question: string;
  readonly intent: string;
  readonly markdown: string;
  readonly confidence: UserConfidence;
  readonly confidenceBasis: string;
  readonly empty: boolean;
  readonly citedRecordIds: readonly string[];
  readonly gaps: readonly AgentDemoGap[];
  readonly unknowns: readonly string[];
  readonly assumptions: readonly string[];
}

/** Static fixture served as `/agent-demo.json` on the public preview origin. */
export interface AgentDemo {
  readonly schemaVersion: number;
  readonly source: string;
  readonly workspace: string;
  readonly runId?: string;
  readonly generatedAt: string;
  readonly recorded: AgentDemoRecording;
  readonly answers: readonly AgentDemoAnswer[];
}

const PERSONAS: readonly AgentDemoPersona[] = [
  "generic",
  "security",
  "architect",
  "operations",
  "finops",
];

const CONFIDENCES: readonly UserConfidence[] = [
  "Verified",
  "Likely",
  "Assumed",
  "Unknown",
];

/**
 * Parse and normalise `agent-demo.json`. Returns `null` when the payload is
 * absent or too malformed to trust — never throws.
 */
export function adaptAgentDemo(input: unknown): AgentDemo | null {
  if (!isRecord(input)) return null;
  if (numberOr(input.schema_version, 0) !== 1) return null;

  const rawAnswers = Array.isArray(input.answers) ? input.answers : [];
  const answers = rawAnswers
    .map(adaptAnswer)
    .filter((a): a is AgentDemoAnswer => a !== null);
  if (answers.length === 0) return null;

  const recorded = adaptRecording(input.recorded);
  if (!recorded) return null;

  const runIdRaw = typeof input.run_id === "string" ? input.run_id.trim() : "";
  const generatedAt =
    typeof input.generated_at === "string" ? input.generated_at : "";

  return {
    schemaVersion: 1,
    source: stringOr(input.source, "recorded-deterministic"),
    workspace: stringOr(input.workspace, ""),
    ...(runIdRaw ? { runId: runIdRaw } : {}),
    generatedAt,
    recorded,
    answers,
  };
}

function adaptRecording(raw: unknown): AgentDemoRecording | null {
  if (!isRecord(raw)) return null;
  const evaluatedAt =
    typeof raw.evaluated_at === "string" ? raw.evaluated_at : "";
  if (!evaluatedAt) return null;
  return {
    evaluatedAt,
    evaluationSchemaVersion: numberOr(raw.evaluation_schema_version, 1),
    turnsRecorded: numberOr(raw.turns_recorded, 0),
    modelCalls: numberOr(raw.model_calls, 0),
    gatesPassed: numberOr(raw.gates_passed, 0),
    gatesEvaluated: numberOr(raw.gates_evaluated, 0),
  };
}

function adaptAnswer(raw: unknown): AgentDemoAnswer | null {
  if (!isRecord(raw)) return null;
  const key =
    typeof raw.key === "string" && raw.key.length > 0 ? raw.key : null;
  const question = typeof raw.question === "string" ? raw.question : null;
  const markdown = typeof raw.markdown === "string" ? raw.markdown : null;
  if (!key || !question || !markdown) return null;

  const persona = PERSONAS.find((p) => p === raw.persona) ?? "generic";
  const confidence = CONFIDENCES.find((c) => c === raw.confidence) ?? "Unknown";

  return {
    key,
    persona,
    question,
    intent: stringOr(raw.intent, "unknown"),
    markdown,
    confidence,
    confidenceBasis:
      typeof raw.confidence_basis === "string"
        ? raw.confidence_basis
        : raw.confidence_basis === null || raw.confidence_basis === undefined
          ? ""
          : "",
    empty: Boolean(raw.empty),
    citedRecordIds: stringList(raw.cited_record_ids),
    gaps: gapList(raw.gaps),
    unknowns: stringList(raw.unknowns),
    assumptions: stringList(raw.assumptions),
  };
}

function gapList(raw: unknown): readonly AgentDemoGap[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(adaptGap).filter((g): g is AgentDemoGap => g !== null);
}

function adaptGap(raw: unknown): AgentDemoGap | null {
  if (typeof raw === "string" && raw.trim()) {
    return { reason: raw.trim(), detail: "" };
  }
  if (!isRecord(raw)) return null;
  const reason = typeof raw.reason === "string" ? raw.reason.trim() : "";
  const detail = typeof raw.detail === "string" ? raw.detail.trim() : "";
  if (!reason && !detail) return null;
  return { reason: reason || "unknown", detail };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function stringOr(v: unknown, fallback: string): string {
  return typeof v === "string" && v.length > 0 ? v : fallback;
}

function stringList(v: unknown): readonly string[] {
  return Array.isArray(v)
    ? v.filter(
        (item): item is string => typeof item === "string" && item.length > 0,
      )
    : [];
}

function numberOr(v: unknown, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}
